/* ========================================================================
   © 2026 SpeedToLead Framework. All rights reserved.
   ======================================================================== */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck, Activity, Phone, Check } from "lucide-react";
import { WIZARD } from "@/constants/testIds";
import { api } from "@/lib/api";
import StripeBuyButton from "@/components/StripeBuyButton";

const STEPS = ["Website", "Phone", "Owner", "Neighborhoods", "FAQs", "Diagnostic"];

const RATING_LABEL = {
    fast: "Fast",
    moderate: "Moderate",
    slow: "Slow",
    unreachable: "Unreachable",
};

const RATING_STYLE = {
    fast: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    moderate: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    slow: "border-primary/50 bg-primary/10 text-primary",
    unreachable: "border-zinc-500/40 bg-zinc-500/10 text-zinc-300",
};

export default function Wizard({ onPaid }) {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState({
        siteUrl: "",
        businessPhone: "",
        ownerName: "",
        email: "",
        neighborhoods: "",
        faq1: "",
        faq2: "",
        faq3: "",
    });
    const [scanning, setScanning] = useState(false);
    const [scanStage, setScanStage] = useState("");
    const [result, setResult] = useState(null);
    const [profile, setProfile] = useState(null);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [plans, setPlans] = useState(null);
    const selectedPlan = "operator"; // unified $149 setup + $79/mo plan

    useEffect(() => {
        api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
    }, []);

    // Persist pending profile id so /checkout/success can complete activation
    useEffect(() => {
        if (profile?.id && selectedPlan) {
            try {
                localStorage.setItem(
                    "speedtolead.pendingCheckout",
                    JSON.stringify({
                        profileId: profile.id,
                        plan: selectedPlan,
                        ts: Date.now(),
                    }),
                );
            } catch {
                /* ignore localStorage errors */
            }
        }
    }, [profile, selectedPlan]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const canNext = () => {
        if (step === 0) return /\w+\.\w+/.test(form.siteUrl);
        if (step === 1) return form.businessPhone.replace(/\D/g, "").length >= 7;
        if (step === 2) return form.ownerName.trim().length > 1 && /.+@.+\..+/.test(form.email);
        if (step === 3) return form.neighborhoods.trim().length > 1;
        if (step === 4) return form.faq1.trim().length > 1;
        return true;
    };

    const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
    const prev = () => setStep((s) => Math.max(0, s - 1));

    const runTest = async () => {
        setScanning(true);
        setResult(null);
        try {
            setScanStage("Resolving DNS and reaching the host...");
            // register profile in parallel with the latency probe so dashboard data is ready
            const registerPromise = api.post("/users/register-intent", {
                email: form.email,
                siteUrl: form.siteUrl,
                businessPhone: form.businessPhone,
                ownerName: form.ownerName,
                neighborhoods: form.neighborhoods,
                faqs: [form.faq1, form.faq2, form.faq3].filter(Boolean),
            });

            await new Promise((r) => setTimeout(r, 350));
            setScanStage("Measuring real server response interval (HTTPS GET)...");
            const diagPromise = api.post("/diagnose", { url: form.siteUrl });

            const [reg, diag] = await Promise.all([registerPromise, diagPromise]);
            setScanStage("Compiling results...");
            await new Promise((r) => setTimeout(r, 250));

            setProfile(reg.data);
            setResult(diag.data);
            setScanning(false);
            setScanStage("");
        } catch (err) {
            setScanning(false);
            setScanStage("");
            toast.error("Diagnostic failed. Please re-check your URL and try again.");
            console.error(err);
        }
    };

    const startCheckout = async () => {
        if (!profile) return;
        setCheckoutLoading(true);
        try {
            const { data } = await api.post("/billing/checkout", {
                userProfileId: profile.id,
                originUrl: window.location.origin,
                plan: selectedPlan,
            });
            if (data.alreadyPaid && data.webhookToken) {
                onPaid?.(data.webhookToken);
                return;
            }
            if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error("No checkout url");
            }
        } catch (err) {
            console.error(err);
            toast.error("Could not start checkout. Please retry.");
            setCheckoutLoading(false);
        }
    };

    return (
        <section
            id="diagnose"
            data-testid={WIZARD.panel}
            className="relative border border-border bg-card p-6 md:p-10 animate-slideUp"
            aria-labelledby="wizard-heading"
        >
            <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <p className="font-mono text-xs uppercase tracking-[0.28em] text-primary">
                        ▪ Communications Latency Simulator
                    </p>
                    <h2 id="wizard-heading" className="font-display text-3xl font-bold tracking-tight md:text-4xl">
                        Real diagnostic. No fiction.
                    </h2>
                    <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                        We perform an actual HTTPS request against your site and report the true round-trip time.
                    </p>
                </div>
                <div className="w-full md:w-72">
                    <p
                        className="mb-2 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground"
                        data-testid={WIZARD.progress}
                    >
                        Step {Math.min(step + 1, STEPS.length)} of {STEPS.length} · {STEPS[step]}
                    </p>
                    <Progress
                        value={((step + 1) / STEPS.length) * 100}
                        className="h-1 bg-muted [&>div]:bg-primary"
                    />
                </div>
            </div>

            {/* Steps */}
            {step === 0 && (
                <Field label="Your website URL" htmlFor="siteUrl">
                    <Input
                        id="siteUrl"
                        data-testid={WIZARD.inputUrl}
                        value={form.siteUrl}
                        onChange={set("siteUrl")}
                        placeholder="www.fixmysitekit.com"
                        autoFocus
                    />
                    <Hint>We accept root domains or full URLs. We never store credentials.</Hint>
                </Field>
            )}

            {step === 1 && (
                <Field label="Business mobile phone" htmlFor="phone">
                    <Input
                        id="phone"
                        type="tel"
                        data-testid={WIZARD.inputPhone}
                        value={form.businessPhone}
                        onChange={set("businessPhone")}
                        placeholder="+1 (555) 010-2030"
                        autoFocus
                    />
                    <Hint>This is the phone we will ring the moment a lead form is submitted.</Hint>
                </Field>
            )}

            {step === 2 && (
                <div className="grid gap-6 md:grid-cols-2">
                    <Field label="Owner / operator name" htmlFor="owner">
                        <Input
                            id="owner"
                            data-testid={WIZARD.inputOwner}
                            value={form.ownerName}
                            onChange={set("ownerName")}
                            placeholder="Sam Rivera"
                            autoFocus
                        />
                    </Field>
                    <Field label="Best email for receipts" htmlFor="email">
                        <Input
                            id="email"
                            type="email"
                            data-testid={WIZARD.inputEmail}
                            value={form.email}
                            onChange={set("email")}
                            placeholder="sam@yourcompany.com"
                        />
                    </Field>
                </div>
            )}

            {step === 3 && (
                <Field label="Target neighborhoods / service area" htmlFor="hoods">
                    <Textarea
                        id="hoods"
                        data-testid={WIZARD.inputNeighborhoods}
                        rows={3}
                        value={form.neighborhoods}
                        onChange={set("neighborhoods")}
                        placeholder="Austin · East Riverside · Mueller · Pflugerville"
                        autoFocus
                    />
                </Field>
            )}

            {step === 4 && (
                <div className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                        The three most common questions your callers actually ask you. These get fed
                        into your AI ad copy and your inbound-call greeting script.
                    </p>
                    <Field label="Top inbound FAQ #1" htmlFor="faq1">
                        <Input
                            id="faq1"
                            data-testid={WIZARD.inputFaq1}
                            value={form.faq1}
                            onChange={set("faq1")}
                            placeholder="What is your typical response time?"
                            autoFocus
                        />
                    </Field>
                    <Field label="FAQ #2 (optional)" htmlFor="faq2">
                        <Input
                            id="faq2"
                            data-testid={WIZARD.inputFaq2}
                            value={form.faq2}
                            onChange={set("faq2")}
                            placeholder="What areas or zip codes do you serve?"
                        />
                    </Field>
                    <Field label="FAQ #3 (optional)" htmlFor="faq3">
                        <Input
                            id="faq3"
                            data-testid={WIZARD.inputFaq3}
                            value={form.faq3}
                            onChange={set("faq3")}
                            placeholder="Do you offer free estimates or after-hours service?"
                        />
                    </Field>
                </div>
            )}

            {step === 5 && (
                <div className="space-y-6">
                    <div
                        aria-live="polite"
                        data-testid={WIZARD.statusLive}
                        className="rounded-none border border-border bg-zinc-950/60 p-4 font-mono text-xs text-muted-foreground"
                    >
                        {scanning ? (
                            <span className="flex items-center gap-2 text-foreground">
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" aria-hidden="true" />
                                {scanStage || "Evaluating internal form processing latency..."}
                            </span>
                        ) : result ? (
                            <span>Scan complete · {new Date(result.testedAt).toLocaleTimeString()}</span>
                        ) : (
                            <span>Ready to scan {form.siteUrl || "your site"}.</span>
                        )}
                    </div>

                    {!result && (
                        <Button
                            data-testid={WIZARD.btnRunTest}
                            onClick={runTest}
                            disabled={scanning}
                            className="rounded-none bg-primary px-6 py-6 text-base font-semibold uppercase tracking-[0.18em] hover:bg-primary/90"
                        >
                            {scanning ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Activity className="mr-2 h-4 w-4" />
                            )}
                            Run Real Speed Test
                        </Button>
                    )}

                    {result && (
                        <div data-testid={WIZARD.results} className="space-y-5 animate-slideUp">
                            <div className="grid gap-4 md:grid-cols-3">
                                <Metric label="Server Response" value={result.reachable ? `${result.serverResponseLatencyMs} ms` : "—"} />
                                <Metric label="HTTP Status" value={result.statusCode ?? "unreachable"} />
                                <div className={`border p-4 ${RATING_STYLE[result.configurationRating]}`}>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">Rating</p>
                                    <p className="mt-1 font-display text-2xl font-bold">
                                        {RATING_LABEL[result.configurationRating]}
                                    </p>
                                </div>
                            </div>

                            <div className="border border-border bg-zinc-950/50 p-5">
                                <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                                    ▪ Analyst Note
                                </p>
                                <p className="mt-2 text-sm text-foreground/90">
                                    {result.reachable
                                        ? result.configurationRating === "fast"
                                            ? "Your site responds quickly. The bottleneck is human follow-up, not infrastructure. SpeedToLead closes that gap by ringing your phone before the lead closes the tab."
                                            : result.configurationRating === "moderate"
                                            ? "Your site responds in a reasonable window. Most lost leads occur after the form submits — when manual email triage delays callbacks beyond five minutes."
                                            : "Your server responded slowly today. Even with a perfect site, the average human reply time on email-routed leads is 45+ minutes. SpeedToLead closes that loop."
                                        : "We couldn't reach your site from this region. That's a separate problem — but SpeedToLead's webhook will still route any new inbound form into a phone call to your line."}
                                </p>
                            </div>

                            <div className="space-y-4">
                                <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                                    ▪ Plan · SpeedToLead Operator
                                </p>
                                <article className="border border-primary bg-primary/10 p-5">
                                    <div className="flex items-baseline justify-between">
                                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                                            SpeedToLead Operator
                                        </p>
                                        <span className="bg-primary px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-primary-foreground">
                                            Setup + Monthly
                                        </span>
                                    </div>
                                    <p className="mt-2 font-display text-3xl font-bold tracking-tight">
                                        <span className="text-primary">$149</span>
                                        <span className="ml-2 text-sm font-medium text-muted-foreground">
                                            setup
                                        </span>
                                        <span className="mx-2 text-muted-foreground">+</span>
                                        <span className="text-primary">$79</span>
                                        <span className="ml-2 text-sm font-medium text-muted-foreground">
                                            / month
                                        </span>
                                    </p>
                                    <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                        Billed today · <span className="text-foreground">$228.00</span> · Then $79 every 30 days
                                    </p>
                                    <ul className="mt-4 space-y-2 text-sm text-foreground/90">
                                        {[
                                            "Lifetime webhook URL",
                                            "500 voice-relay minutes / month",
                                            "Real-time speed diagnostics",
                                            "WordPress / Webflow / Shopify snippets",
                                            "AI Meta ad copy generator",
                                            "Email lead receipts",
                                            "$0.10 / extra minute (auto-billed)",
                                        ].map((f) => (
                                            <li key={f} className="flex items-start gap-2">
                                                <Check className="mt-0.5 h-3.5 w-3.5 flex-none text-primary" aria-hidden="true" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>

                                <div className="space-y-3 border border-primary/30 bg-primary/5 p-5">
                                    <div className="flex items-center justify-between gap-3">
                                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                                            ▪ Checkout · $228 today, then $79/mo
                                        </p>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            Powered by Stripe
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Your workspace unlocks automatically the moment Stripe confirms payment.
                                    </p>
                                    <StripeBuyButton
                                        buyButtonId={
                                            plans?.plans?.find((p) => p.id === "operator")?.buy_button_id
                                        }
                                        customerEmail={profile?.email}
                                        clientReferenceId={profile?.id}
                                    />
                                </div>

                                {/* Refund Guarantee badge */}
                                <div
                                    data-testid="refund-guarantee"
                                    className="flex items-start gap-3 border border-emerald-500/40 bg-emerald-500/5 p-4"
                                >
                                    <ShieldCheck
                                        className="mt-0.5 h-5 w-5 flex-none text-emerald-400"
                                        aria-hidden="true"
                                    />
                                    <div>
                                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-300">
                                            Zero-Risk Refund Guarantee
                                        </p>
                                        <p className="mt-1 text-sm text-foreground/90">
                                            If your webhook never places a single relayed call, request a full
                                            $228 refund from your dashboard — no questions. The minute one call
                                            goes through, your subscription locks in at $79/month.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                                <span>
                                    Voice relay rings your business phone via a live Twilio gateway the
                                    instant a form is submitted.
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Footer controls */}
            {step < 5 && (
                <div className="mt-10 flex items-center justify-between border-t border-border pt-6">
                    <Button
                        data-testid={WIZARD.btnPrev}
                        variant="ghost"
                        onClick={prev}
                        disabled={step === 0}
                        className="rounded-none text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                    <Button
                        data-testid={WIZARD.btnNext}
                        onClick={next}
                        disabled={!canNext()}
                        className="rounded-none bg-primary px-6 py-5 text-sm font-semibold uppercase tracking-[0.18em] hover:bg-primary/90"
                    >
                        Next <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </div>
            )}
        </section>
    );
}

function Field({ label, htmlFor, children }) {
    return (
        <div className="space-y-2">
            <Label htmlFor={htmlFor} className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                {label}
            </Label>
            {children}
        </div>
    );
}

function Hint({ children }) {
    return <p className="text-xs text-muted-foreground">{children}</p>;
}

function Metric({ label, value }) {
    return (
        <div className="border border-border bg-zinc-950/50 p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
            <p className="mt-1 font-display text-2xl font-bold tabular-nums">{value}</p>
        </div>
    );
}

function PlanCard({ testid, active, onSelect, label, price, cadence, badge, features }) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={testid}
            onClick={onSelect}
            className={`relative text-left transition-colors duration-150 border p-5 focus-visible:outline-none ${
                active
                    ? "border-primary bg-primary/10"
                    : "border-border bg-zinc-950/50 hover:border-primary/40 hover:bg-zinc-950"
            }`}
        >
            {badge && (
                <span className="absolute -top-3 left-4 bg-primary px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-primary-foreground">
                    {badge}
                </span>
            )}
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                {label}
            </p>
            <p className="mt-2 font-display text-3xl font-bold tracking-tight">
                <span className={active ? "text-primary" : "text-foreground"}>{price}</span>
                <span className="ml-2 text-sm font-medium text-muted-foreground">{cadence}</span>
            </p>
            <ul className="mt-4 space-y-2 text-sm text-foreground/90">
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                        <Check
                            className={`mt-0.5 h-3.5 w-3.5 flex-none ${
                                active ? "text-primary" : "text-muted-foreground"
                            }`}
                            aria-hidden="true"
                        />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
        </button>
    );
}
